import { api } from './api'

export interface Package {
  id: string
  name: string
  type: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'CUSTOM'
  price: number
  description: string
  includes: string[]
  excludes: string[]
  isPopular: boolean
  sortOrder: number
  maxDistance?: number
  serviceAreas: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface PackageRecommendation {
  packages: Package[]
  assessment: {
    urgencyLevel: string
    locationType: string
    currentLocation: string
    preferredBurialArea: string
  }
  timeline: {
    ambulancePickup: {
      timeframe: string
      priority: string
    }
    documentSubmission: {
      deadline: string
      required: string[]
    }
    burialArrangement: {
      timeframe: string
      preferredArea: string
    }
    islamicGuidelines: {
      burialWithin24Hours: string
      prayerRequirements: string
    }
  }
  providerMatches: {
    ambulances: any[]
    ustadz: any[]
    burialGrounds: any[]
  }
  nextSteps: string[]
}

class PackagesService {
  async getAllPackages(activeOnly = true): Promise<Package[]> {
    const response = await api.get('/packages', {
      params: { activeOnly }
    })
    return response.data
  }

  async getPackageById(id: string): Promise<Package> {
    const response = await api.get(`/packages/${id}`)
    return response.data
  }

  async getPopularPackages(): Promise<Package[]> {
    const response = await api.get('/packages/popular')
    return response.data
  }

  async getRecommendedPackages(
    urgencyLevel?: string,
    locationArea?: string
  ): Promise<Package[]> {
    const response = await api.get('/packages/recommended', {
      params: { urgencyLevel, locationArea }
    })
    return response.data
  }

  async getPackagesByType(type: string): Promise<Package[]> {
    const response = await api.get(`/packages/type/${type}`)
    return response.data
  }

  async comparePackages(packageIds: string[]): Promise<any> {
    const response = await api.get('/packages/compare', {
      params: { packageIds }
    })
    return response.data
  }

  async seedDefaultPackages(): Promise<Package[]> {
    const response = await api.post('/packages/seed')
    return response.data
  }

  async createPackage(packageData: Partial<Package>): Promise<Package> {
    const response = await api.post('/packages', packageData)
    return response.data
  }

  async updatePackage(id: string, packageData: Partial<Package>): Promise<Package> {
    const response = await api.patch(`/packages/${id}`, packageData)
    return response.data
  }

  async deletePackage(id: string): Promise<void> {
    await api.delete(`/packages/${id}`)
  }

  async getAllRegions(): Promise<string[]> {
    const response = await api.get('/packages/regions')
    return response.data
  }

  async getPackagesByRegion(regionName: string): Promise<Package[]> {
    const response = await api.get(`/packages/region/${regionName}`)
    return response.data
  }

  async clonePackageToRegion(packageId: string, targetRegion: string): Promise<Package> {
    const response = await api.post(`/packages/clone/${packageId}`, null, {
      params: { targetRegion }
    })
    return response.data
  }

  async getPackageStatistics(): Promise<any> {
    const response = await api.get('/packages/admin/stats')
    return response.data
  }
}

export const packagesService = new PackagesService()